import { useState } from 'react';
import { Project } from '../../../types';
import { supabase } from '../../../lib/supabase';

export function useProjectSave(
  project: Project,
  isNew: boolean,
  onSave: (project: Project) => void
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (editedProject: Project) => {
    setLoading(true);
    setError(null);

    try {
      if (!editedProject.projectName?.trim()) {
        throw new Error('Project name is required');
      }

      if (isNew) {
        const { data: newProject, error: createError } = await supabase
          .from('projects')
          .insert({
            project_name: editedProject.projectName,
            description: editedProject.description,
            customer_id: editedProject.customer.id,
            year: editedProject.year,
            month: editedProject.month,
            duration_id: editedProject.duration?.id,
            budget: editedProject.budget,
            workload: editedProject.workload,
            featured: editedProject.featured
          })
          .select()
          .single();

        if (createError) throw createError;
        if (!newProject) throw new Error('Failed to create project');

        // Handle images, tags, and staff relationships
        await Promise.all([
          handleImages(newProject.id, editedProject.images),
          handleTags(newProject.id, editedProject.tags),
          handleStaff(newProject.id, editedProject.staff)
        ]);

        const { data: finalProject, error: finalError } = await fetchFinalProject(newProject.id);
        if (finalError) throw finalError;
        if (!finalProject) throw new Error('Failed to fetch created project');

        onSave(transformProject(finalProject));
      } else {
        const { data: existingProject, error: checkError } = await supabase
          .from('projects')
          .select('id')
          .eq('id', project.id)
          .maybeSingle();

        if (checkError) throw checkError;
        if (!existingProject) throw new Error('Project not found');

        const { error: updateError } = await supabase
          .from('projects')
          .update({
            project_name: editedProject.projectName,
            description: editedProject.description,
            customer_id: editedProject.customer.id,
            year: editedProject.year,
            month: editedProject.month,
            duration_id: editedProject.duration?.id,
            budget: editedProject.budget,
            workload: editedProject.workload,
            featured: editedProject.featured
          })
          .eq('id', project.id);

        if (updateError) throw updateError;

        // Handle images, tags, and staff relationships
        await Promise.all([
          handleImages(project.id, editedProject.images),
          handleTags(project.id, editedProject.tags),
          handleStaff(project.id, editedProject.staff)
        ]);

        const { data: finalProject, error: finalError } = await fetchFinalProject(project.id);
        if (finalError) throw finalError;
        if (!finalProject) throw new Error('Failed to fetch updated project');

        onSave(transformProject(finalProject));
      }
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleImages = async (projectId: string, images: any[]) => {
    const { data: currentImages } = await supabase
      .from('project_images')
      .select('id, url, video_url, media_type')
      .eq('project_id', projectId);

    const currentImageIds = new Set(currentImages?.map(img => img.id) || []);
    const newImageIds = new Set(images.map(img => img.id));

    // Handle image deletions
    const imagesToRemove = Array.from(currentImageIds)
      .filter(id => !newImageIds.has(id));

    if (imagesToRemove.length > 0) {
      const imagesToDelete = currentImages
        ?.filter(img => imagesToRemove.includes(img.id))
        .filter(img => img.media_type !== 'youtube')
        .map(img => {
          const url = new URL(img.media_type === 'video' ? img.video_url : img.url);
          return url.pathname.split('/').pop();
        })
        .filter((path): path is string => path !== undefined);

      if (imagesToDelete && imagesToDelete.length > 0) {
        await supabase.storage
          .from('project-images')
          .remove(imagesToDelete);
      }

      await supabase
        .from('project_images')
        .delete()
        .eq('project_id', projectId)
        .in('id', imagesToRemove);
    }

    // Handle new images
    const imagesToAdd = images
      .filter(img => !currentImageIds.has(img.id))
      .map(img => ({
        project_id: projectId,
        url: img.url,
        video_url: img.video_url,
        media_type: img.media_type,
        display_order: img.display_order
      }));

    if (imagesToAdd.length > 0) {
      await supabase
        .from('project_images')
        .insert(imagesToAdd);
    }
  };

  const handleTags = async (projectId: string, tags: any[]) => {
    const { data: currentTags } = await supabase
      .from('project_tags')
      .select('tag_id')
      .eq('project_id', projectId);

    const currentTagIds = new Set(currentTags?.map(t => t.tag_id) || []);
    const newTagIds = new Set(tags.map(t => t.id));

    // Handle tag removals
    const tagsToRemove = Array.from(currentTagIds)
      .filter(tagId => !newTagIds.has(tagId));

    if (tagsToRemove.length > 0) {
      await supabase
        .from('project_tags')
        .delete()
        .eq('project_id', projectId)
        .in('tag_id', tagsToRemove);
    }

    // Handle new tags
    const tagsToAdd = tags
      .filter(tag => !currentTagIds.has(tag.id))
      .map(tag => ({
        project_id: projectId,
        tag_id: tag.id
      }));

    if (tagsToAdd.length > 0) {
      await supabase
        .from('project_tags')
        .insert(tagsToAdd);
    }
  };

  const handleStaff = async (projectId: string, staff: any[]) => {
    const { data: currentStaff } = await supabase
      .from('project_staff')
      .select('staff_id')
      .eq('project_id', projectId);

    const currentStaffIds = new Set(currentStaff?.map(s => s.staff_id) || []);
    const newStaffIds = new Set(staff.map(s => s.id));

    // Handle staff removals
    const staffToRemove = Array.from(currentStaffIds)
      .filter(staffId => !newStaffIds.has(staffId));

    if (staffToRemove.length > 0) {
      await supabase
        .from('project_staff')
        .delete()
        .eq('project_id', projectId)
        .in('staff_id', staffToRemove);
    }

    // Handle new staff
    const staffToAdd = staff
      .filter(member => !currentStaffIds.has(member.id))
      .map(member => ({
        project_id: projectId,
        staff_id: member.id
      }));

    if (staffToAdd.length > 0) {
      await supabase
        .from('project_staff')
        .insert(staffToAdd);
    }
  };

  const fetchFinalProject = async (projectId: string) => {
    return await supabase
      .from('projects')
      .select(`
        *,
        customer:customers (
          id,
          name
        ),
        duration:durations (
          id,
          name
        ),
        images:project_images (
          id,
          url,
          video_url,
          media_type,
          display_order,
          created_at
        ),
        tags:project_tags (
          tag:tags (
            id,
            name
          )
        ),
        staff:project_staff (
          staff:staff (
            id,
            name
          )
        )
      `)
      .eq('id', projectId)
      .single();
  };

  const transformProject = (data: any): Project => ({
    id: data.id,
    projectName: data.project_name,
    description: data.description,
    year: data.year,
    month: data.month,
    duration: data.duration,
    budget: data.budget,
    workload: data.workload,
    featured: data.featured,
    customer: data.customer,
    images: data.images,
    tags: data.tags.map((t: any) => t.tag),
    staff: data.staff.map((s: any) => s.staff),
    created_at: data.created_at,
    updated_at: data.updated_at
  });

  return {
    loading,
    error,
    handleSave,
    setError
  };
}